import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import type { IconName } from '~/components';

import { useRefreshPeppol, useUser } from '~/api/user';
import { Button, Card, ExternalLink, Icon, Infotip, StatusTag, Text } from '~/components';
import { getUserPeppolStatus } from '~/utils/user';

import {
  boldStyle,
  buttonStyle,
  cardStyle,
  detailsStyle,
  disclaimerStyle,
  iconWrapperStyle,
  labelStyle,
  statusStyle,
  statusTagStyle,
  valueRowStyle,
  valueStyle,
} from './Peppol.css';

export const Peppol = () => {
  const { data: user } = useUser();
  const mutation = useRefreshPeppol();

  const { t } = useTranslation(['common', 'settings']);

  const peppolStatus = getUserPeppolStatus(user);

  const statusIcon = match(peppolStatus)
    .returnType<IconName>()
    .with('Active', () => 'Check')
    .with('ActiveSelfBillingFallback', () => 'Warning')
    .with('NotRegistered', () => 'Error')
    .exhaustive();

  return (
    <>
      <Card.Root className={cardStyle} size="small" variant="white">
        <span className={iconWrapperStyle}>
          <Icon name="Peppol" size={33} />
        </span>

        <div className={detailsStyle}>
          <Text className={labelStyle}>{t('settings:integrations.peppol.fields.peppolId.label')}</Text>

          <div className={valueRowStyle}>
            <Text className={valueStyle}>{user.peppol.id ?? t('settings:integrations.peppol.fields.peppolId.empty')}</Text>

            {user.peppol.id !== null && (
              <Infotip>
                <Trans
                  components={{ email: <ExternalLink target="_self" /> }}
                  i18nKey="settings:integrations.peppol.fields.peppolId.tooltip"
                />
              </Infotip>
            )}
          </div>
        </div>

        <div className={statusStyle}>
          <StatusTag
            className={statusTagStyle}
            icon={statusIcon}
            label={t(`settings:integrations.peppol.fields.status.options.${peppolStatus}`)}
            status={match(peppolStatus)
              .with('Active', () => 'success' as const)
              .with('ActiveSelfBillingFallback', () => 'warning' as const)
              .with('NotRegistered', () => 'danger' as const)
              .exhaustive()}
          />
        </div>
      </Card.Root>

      {peppolStatus === 'ActiveSelfBillingFallback' && (
        <Text className={disclaimerStyle}>
          <Trans
            components={{ bold: <span className={boldStyle} /> }}
            i18nKey="settings:integrations.peppol.disclaimerSelfBillingUnsupported"
          />
        </Text>
      )}

      {peppolStatus === 'NotRegistered' && (
        <Button className={buttonStyle} emphasis="secondary" icon="Refresh" loading={mutation.isPending} onClick={() => mutation.mutate()}>
          {t('settings:integrations.peppol.actions.checkAgain')}
        </Button>
      )}
    </>
  );
};
